import { useCallback, useEffect, useRef, useState } from "react";

export const useColumnResizing = ({ minWidth = 10, maxWidth = 480 } = {}) => {
  const [columnWidths, setColumnWidths] = useState({});
  const resizingRef = useRef(null);

  const updateColumnWidth = useCallback(
    (columnId, width) => {
      if (!columnId) return;
      setColumnWidths((prev) => {
        if (width === null) {
          if (prev[columnId] === undefined) return prev;
          const { [columnId]: _removed, ...rest } = prev;
          return rest;
        }
        const clamped = Math.min(maxWidth, Math.max(minWidth, width));
        if (prev[columnId] === clamped) return prev;
        return { ...prev, [columnId]: clamped };
      });
    },
    [maxWidth, minWidth]
  );

  useEffect(() => {
    const handleMouseMove = (event) => {
      const active = resizingRef.current;
      if (!active) return;
      const delta = event.clientX - active.startX;
      const nextWidth = Math.min(
        maxWidth,
        Math.max(minWidth, active.startWidth + delta)
      );
      updateColumnWidth(active.columnId, nextWidth);
    };

    const endResize = () => {
      if (!resizingRef.current) return;
      resizingRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", endResize);
    window.addEventListener("mouseleave", endResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", endResize);
      window.removeEventListener("mouseleave", endResize);
    };
  }, [maxWidth, minWidth, updateColumnWidth]);

  const startColumnResize = useCallback((columnId, event) => {
    event.preventDefault();
    event.stopPropagation();
    const headerCell = event.currentTarget.closest("th");
    if (!headerCell) return;
    const rect = headerCell.getBoundingClientRect();
    resizingRef.current = {
      columnId,
      startX: event.clientX,
      startWidth: rect.width,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, []);

  return {
    columnWidths,
    updateColumnWidth,
    startColumnResize,
    setColumnWidths,
  };
};
